/* eslint-disable @typescript-eslint/no-explicit-any */
import { createClient } from "@/prismicio";
import { AllDocumentTypes } from "@/prismicio-types";
import { withPrismicProductCacheTag } from "@/lib/revalidate/product-targets";
import { Content } from "@prismicio/client";
import { getPrismicLocale, Locales } from "../i18n";
import { resolveSiteSettings } from "./site-settings";

export const getDocument = async (
  type: AllDocumentTypes["type"],
  uid: string,
  locale: Locales,
  params: object = {}
): Promise<AllDocumentTypes | null> => {
  const client = createClient();
  const documentParams = params as Record<string, any>;
  const fetchOptions =
    type === "product"
      ? withPrismicProductCacheTag(documentParams.fetchOptions, locale, uid)
      : documentParams.fetchOptions;

  try {
    const document = await client.getByUID(type, uid, {
      ...documentParams,
      ...(fetchOptions ? { fetchOptions } : {}),
      lang: getPrismicLocale(locale),
    });

    return document;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

export const getSingle = async <T = AllDocumentTypes,>(
  type: AllDocumentTypes["type"],
  locale: Locales,
  params: object = {}
): Promise<T | null> => {
  const client = createClient();

  try {
    const document = await client.getSingle(type, {
      ...params,
      lang: getPrismicLocale(locale),
    });

    return document as T;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

export const getSiteSettings = async (locale: Locales) => {
  const siteSettings = await getSingle<Content.SiteSettingsDocument>("siteSettings", locale);

  return resolveSiteSettings(siteSettings);
};

export const getByID = async <T = AllDocumentTypes,>(id: string, params: object = {}): Promise<T | null> => {
  const client = createClient();

  try {
    const document = await client.getByID(id, {
      ...params,
    });

    return document as T;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

export const getAllByIDs = async (ids: string[], params: object = {}): Promise<AllDocumentTypes[] | null> => {
  const client = createClient();

  try {
    const documents = await client.getAllByIDs(ids, {
      ...params,
    });

    return documents;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

function handlePrismicError(error: any) {
  if (error.response && error.response.status) {
    // You can access the HTTP status code here
    const statusCode = error.response.status;
    console.warn(`Error: ${statusCode}`);

    switch (statusCode) {
      case 404:
        console.warn("Document not found.");
        break;
      case 401:
        console.warn("Unauthorized - check your Prismic credentials.");
        break;
      case 500:
        console.warn("Internal Server Error - try again later.");
        break;
      default:
        console.warn("An unexpected error occurred:", error.message);
    }
  } else {
    // Handle other types of errors (network issues, etc.)
    console.warn("An error occurred:", error.message);
  }
}

export const getByType = async (type: AllDocumentTypes["type"], locale: Locales, params: object = {}) => {
  const client = createClient();
  try {
    const result = await client.getByType(type, {
      lang: getPrismicLocale(locale),
      ...params,
    });
    return result;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

export const getAllByType = async (type: AllDocumentTypes["type"], locale: Locales | "*", params: object = {}) => {
  const client = createClient();
  try {
    const result = await client.getAllByType(type, {
      lang: locale === "*" ? locale : getPrismicLocale(locale),
      ...params,
    });
    return result;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};

export const getAllByUids = async (
  type: AllDocumentTypes["type"],
  uids: string[],
  locale: Locales,
  params: object = {}
) => {
  const client = createClient();
  try {
    const result = await client.getAllByUIDs(type, uids, {
      lang: getPrismicLocale(locale),
      ...params,
    });
    return result;
  } catch (error: any) {
    handlePrismicError(error);
    return null;
  }
};
